import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Globe, ArrowLeft, Users, FileCheck, Clock, ShieldAlert, Building2, CheckCircle2, XCircle, Eye, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeadlineLight } from '@/components/DeadlineLight';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KPI = ({ icon: Icon, label, value, accent }) => (
  <Card className="bg-white/5 border-white/10">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{label}</p>
          <p className="text-3xl font-semibold text-white">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DPOGlobalDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [riskFilter, setRiskFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [dash, reqs] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dpo/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/dpo/requests`, { withCredentials: true }),
      ]);
      setDashboard(dash.data);
      setRequests(reqs.data.requests || []);
      setSummary(reqs.data.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar painel consolidado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const runBulk = async (action) => {
    if (selected.length === 0) return toast.error('Selecione pedidos primeiro');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/dpo/requests/bulk-action`,
        { request_ids: selected, action },
        { withCredentials: true }
      );
      toast.success(`${data.updated} pedido(s) atualizado(s)`);
      setSelected([]);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na ação em massa');
    }
  };

  const totals = dashboard?.totals || {};
  const visibleRequests = riskFilter === 'all' ? requests : requests.filter((r) => r.risk_level === riskFilter);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="dpo-global-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/10 mb-4 -ml-3" onClick={() => navigate('/dpo/tenants')} data-testid="back-to-selector-button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao seletor
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Painel consolidado
              </h1>
              <Badge className="bg-[#10B981] text-white gap-1"><Globe className="w-3 h-3" /> Global</Badge>
            </div>
            <p className="text-base text-slate-400 mt-2">Visão cross-tenant de todas as organizações sob gestão</p>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-400">A carregar...</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6 mb-10">
              <KPI icon={Building2} label="Organizações" value={totals.tenants || 0} accent="bg-blue-500/15 text-blue-400" />
              <KPI icon={Users} label="Titulares" value={totals.titulares || 0} accent="bg-[#10B981]/15 text-[#10B981]" />
              <KPI icon={FileCheck} label="Consent. ativos" value={totals.consents_active || 0} accent="bg-emerald-500/15 text-emerald-400" />
              <KPI icon={Clock} label="Pedidos pendentes" value={totals.requests_pending || 0} accent="bg-amber-500/15 text-amber-400" />
              <KPI icon={AlertTriangle} label="Prazo em atraso" value={totals.requests_overdue || 0} accent="bg-red-500/15 text-red-400" />
              <KPI icon={ShieldAlert} label="Violações abertas" value={totals.breaches_open || 0} accent="bg-red-500/15 text-red-400" />
            </div>

            <Card className="bg-white/5 border-white/10 mb-8">
              <CardHeader>
                <CardTitle className="text-white text-lg">Por organização</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="dpo-tenant-breakdown-table">
                    <thead className="text-slate-500 border-y border-white/10">
                      <tr>
                        <th className="text-left px-6 py-3 font-medium">Organização</th>
                        <th className="text-left px-6 py-3 font-medium">Plano</th>
                        <th className="text-right px-6 py-3 font-medium">Titulares</th>
                        <th className="text-right px-6 py-3 font-medium">Ativos</th>
                        <th className="text-right px-6 py-3 font-medium">A expirar</th>
                        <th className="text-right px-6 py-3 font-medium">Pendentes</th>
                        <th className="text-right px-6 py-3 font-medium">Em risco</th>
                        <th className="text-right px-6 py-3 font-medium">Em atraso</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboard?.tenants || []).map((t) => (
                        <tr key={t.tenant_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-6 py-4 font-medium text-slate-100">{t.name}</td>
                          <td className="px-6 py-4 uppercase text-xs text-slate-400">{t.plan_key}</td>
                          <td className="px-6 py-4 text-right text-slate-100">{t.titulares}</td>
                          <td className="px-6 py-4 text-right text-slate-100">{t.consents_active}</td>
                          <td className="px-6 py-4 text-right text-slate-100">{t.consents_expiring}</td>
                          <td className="px-6 py-4 text-right text-slate-100">{t.requests_pending}</td>
                          <td className="px-6 py-4 text-right text-amber-400">{t.requests_at_risk || 0}</td>
                          <td className="px-6 py-4 text-right text-red-400 font-semibold">{t.requests_overdue || 0}</td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#10B981] hover:bg-[#10B981]/10"
                              data-testid={`open-tenant-${t.slug || t.tenant_id}`}
                              onClick={async () => {
                                await axios.post(`${BACKEND_URL}/api/dpo/select-tenant`, { tenant_id: t.tenant_id }, { withCredentials: true });
                                navigate('/dpo');
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" /> Abrir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-white text-lg">Pedidos de titulares (cross-tenant)</CardTitle>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[
                      { key: 'all', label: `Todos (${requests.length})`, dot: 'bg-slate-400' },
                      { key: 'overdue', label: `Em atraso (${summary?.overdue || 0})`, dot: 'bg-red-500' },
                      { key: 'critical', label: `Crítico (${summary?.critical || 0})`, dot: 'bg-orange-500' },
                      { key: 'warning', label: `Atenção (${summary?.warning || 0})`, dot: 'bg-amber-400' },
                      { key: 'ok', label: `No prazo (${summary?.ok || 0})`, dot: 'bg-emerald-500' },
                    ].map((chip) => (
                      <button
                        key={chip.key}
                        onClick={() => setRiskFilter(chip.key)}
                        data-testid={`risk-filter-${chip.key}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          riskFilter === chip.key
                            ? 'border-[#10B981] bg-[#10B981]/10 text-white'
                            : 'border-white/10 text-slate-400 hover:border-white/30'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => runBulk('in_review')} data-testid="bulk-in-review-button">
                    <Eye className="w-4 h-4 mr-1" /> Em análise
                  </Button>
                  <Button size="sm" className="bg-[#10B981] hover:bg-[#0ea371]" onClick={() => runBulk('approve')} data-testid="bulk-approve-button">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprovar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulk('reject')} data-testid="bulk-reject-button">
                    <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {visibleRequests.length === 0 ? (
                  <p className="px-6 py-10 text-center text-slate-400">
                    {requests.length === 0
                      ? 'Sem pedidos pendentes em nenhuma organização.'
                      : 'Nenhum pedido neste estado de prazo.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="dpo-requests-table">
                      <thead className="text-slate-500 border-y border-white/10">
                        <tr>
                          <th className="px-6 py-3"></th>
                          <th className="text-left px-6 py-3 font-medium">Organização</th>
                          <th className="text-left px-6 py-3 font-medium">Titular</th>
                          <th className="text-left px-6 py-3 font-medium">Motivo</th>
                          <th className="text-left px-6 py-3 font-medium">Prazo legal</th>
                          <th className="text-left px-6 py-3 font-medium">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRequests.map((r) => (
                          <tr key={r.rtbf_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="px-6 py-4">
                              <Checkbox
                                checked={selected.includes(r.rtbf_id)}
                                onCheckedChange={() => toggle(r.rtbf_id)}
                                data-testid={`request-checkbox-${r.rtbf_id}`}
                              />
                            </td>
                            <td className="px-6 py-4 text-slate-100">{r.tenant_name}</td>
                            <td className="px-6 py-4">
                              <p className="text-slate-100">{r.titular_name || '—'}</p>
                              <p className="text-xs text-slate-500">{r.titular_email}</p>
                            </td>
                            <td className="px-6 py-4 text-slate-300">{r.reason || '—'}</td>
                            <td className="px-6 py-4"><DeadlineLight request={r} dark /></td>
                            <td className="px-6 py-4"><Badge variant="outline" className="border-white/20 text-slate-200">{r.status}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default DPOGlobalDashboard;
